// In-memory cycle counts for demo mode (no Supabase configured). The state
// machine, numbering and blind/visible quantities behave like the database;
// the snapshot is a fixed sample and stock levels are not recalculated.

import type { Page } from "@/components/DataList";
import type {
  CountActionResult,
  CountDocument,
  CountEntry,
  CountLine,
  CountRow,
  CountScope,
  CountStatus,
  CountStatusFilter,
  EnterResult,
  LineFilter,
} from "./api";

const delay = (ms: number) => new Promise((r) => setTimeout(r, ms));
const now = () => new Date().toISOString();
const year = new Date().getFullYear();
let seq = 0;
let counter = 1;

const sample: Omit<CountLine, "id" | "line_no" | "counted_qty" | "difference" | "is_added">[] = [
  { item_code: "BOX-600", item_description: "Karton 600×400", location_code: "A-01-01", lot_code: null, is_new_lot: false, uom_code: "PCS", uom_decimals: 0, system_qty: 120 },
  { item_code: "FOAM-25", item_description: "Pianka 25 mm", location_code: "A-01-01", lot_code: "F-2026-014", is_new_lot: false, uom_code: "M", uom_decimals: 2, system_qty: 48.5 },
  { item_code: "SCREW-M6", item_description: "Śruba M6×20", location_code: "A-01-02", lot_code: null, is_new_lot: false, uom_code: "PCS", uom_decimals: 0, system_qty: 2400 },
];

interface Stored {
  id: string;
  number: string | null;
  status: CountStatus;
  warehouse_id: string;
  scope: CountScope;
  note: string | null;
  created_at: string;
  started_at: string | null;
  posted_at: string | null;
  cancelled_at: string | null;
  lines: { line: Omit<CountLine, "difference">; system: number }[];
}

const counts: Stored[] = [];
const stateError = (status: CountStatus) =>
  Object.assign(new Error(`This cycle count cannot be changed (status: ${status})`), { code: "23514" });
const find = (id: string) => {
  const c = counts.find((x) => x.id === id);
  if (!c) throw Object.assign(new Error("Cycle count not found"), { code: "P0002" });
  return c;
};
const counted = (c: Stored) => c.lines.filter((l) => l.line.counted_qty !== null).length;

function toRow(c: Stored): CountRow {
  return {
    id: c.id,
    number: c.number,
    status: c.status,
    note: c.note,
    line_count: c.lines.length,
    counted_count: counted(c),
    location_count: c.started_at ? 2 : 0,
    created_at: c.created_at,
    started_at: c.started_at,
    posted_at: c.posted_at,
    warehouse: { code: c.warehouse_id.replace(/^wh-/, ""), name: "" },
    company: { code: "PL01" },
  };
}

export const demoApi = {
  async list(status: CountStatusFilter, cursor: string | null): Promise<Page<CountRow>> {
    await delay(60);
    const rows = counts
      .filter((c) => status === "all" || c.status === status)
      .sort((a, b) => (a.created_at < b.created_at ? 1 : -1))
      .filter((c) => !cursor || `${c.created_at}|${c.id}` < cursor)
      .map(toRow);
    return { rows: rows.slice(0, 50), nextCursor: null };
  },
  async get(id: string): Promise<CountDocument | null> {
    await delay(40);
    const c = counts.find((x) => x.id === id);
    if (!c) return null;
    return {
      id: c.id,
      number: c.number,
      status: c.status,
      company_id: "demo-pl01",
      company_code: "PL01",
      warehouse_id: c.warehouse_id,
      warehouse_code: c.warehouse_id.replace(/^wh-/, ""),
      warehouse_name: "",
      scope: c.scope,
      note: c.note,
      line_count: c.lines.length,
      counted_count: counted(c),
      location_count: c.started_at ? 2 : 0,
      show_system: true,
      created_at: c.created_at,
      started_at: c.started_at,
      posted_at: c.posted_at,
      cancelled_at: c.cancelled_at,
      updated_at: c.created_at,
      locations: c.started_at
        ? [
            { id: "loc-A-01-01", code: "A-01-01" },
            { id: "loc-A-01-02", code: "A-01-02" },
          ]
        : [],
    };
  },
  async lines(id: string, filter: LineFilter, after: number, limit: number): Promise<CountLine[]> {
    await delay(40);
    return find(id)
      .lines.map(({ line, system }) => ({
        ...line,
        difference: line.counted_qty === null ? null : Number(line.counted_qty) - system,
      }))
      .filter((l) => l.line_no > after)
      .filter((l) =>
        filter === "uncounted" ? l.counted_qty === null : filter === "differences" ? l.difference !== 0 : true,
      )
      .slice(0, limit);
  },
  async save(id: string | null, warehouseId: string, scope: CountScope, note: string): Promise<string> {
    await delay(80);
    if (id) {
      const c = find(id);
      if (c.status !== "draft") throw stateError(c.status);
      Object.assign(c, { warehouse_id: warehouseId, scope, note: note || null });
      return id;
    }
    const c: Stored = {
      id: `cnt-${++seq}`,
      number: null,
      status: "draft",
      warehouse_id: warehouseId,
      scope,
      note: note || null,
      created_at: now(),
      started_at: null,
      posted_at: null,
      cancelled_at: null,
      lines: [],
    };
    counts.push(c);
    return c.id;
  },
  async start(id: string): Promise<CountActionResult> {
    await delay(120);
    const c = find(id);
    if (c.status !== "draft") throw stateError(c.status);
    c.lines = sample.map((s, i) => ({
      line: { ...s, id: `${id}-l${i + 1}`, line_no: i + 1, counted_qty: null, is_added: false },
      system: Number(s.system_qty),
    }));
    Object.assign(c, {
      status: "in_progress",
      number: `CNT-${year}-${String(counter++).padStart(5, "0")}`,
      started_at: now(),
    });
    return { id, number: c.number, status: "in_progress", line_count: c.lines.length, location_count: 2 };
  },
  async enter(id: string, entries: CountEntry[]): Promise<EnterResult> {
    await delay(60);
    const c = find(id);
    if (c.status !== "in_progress") throw stateError(c.status);
    const touched: EnterResult["lines"] = [];
    for (const entry of entries) {
      // Demo data has no serial items; a serial entry counts its units.
      const e = "serials" in entry ? { line_id: entry.line_id, qty: String(entry.serials.length) } : entry;
      if (Number(e.qty) < 0) throw Object.assign(new Error("Entry 1: counted quantity cannot be negative"), { code: "23514" });
      const target = "line_id" in e ? c.lines.find((l) => l.line.id === e.line_id) : undefined;
      if (target) {
        target.line.counted_qty = e.qty;
        touched.push({ id: target.line.id, line_no: target.line.line_no, counted_qty: e.qty, is_added: false });
      } else if (!("line_id" in e)) {
        const line_no = c.lines.length + 1;
        const line = {
          id: `${id}-l${line_no}`,
          line_no,
          item_code: e.item_id.replace(/^item-/, ""),
          item_description: "",
          location_code: e.location_id.replace(/^loc-/, ""),
          lot_code: e.lot_code ?? null,
          is_new_lot: !!e.lot_code,
          uom_code: "PCS",
          uom_decimals: 0,
          system_qty: 0,
          counted_qty: e.qty,
          is_added: true,
        };
        c.lines.push({ line, system: 0 });
        touched.push({ id: line.id, line_no, counted_qty: e.qty, is_added: true });
      }
    }
    return { id, line_count: c.lines.length, counted_count: counted(c), added: 0, lines: touched };
  },
  async post(id: string): Promise<CountActionResult> {
    await delay(150);
    const c = find(id);
    if (c.status !== "in_progress") throw stateError(c.status);
    const open = c.lines.length - counted(c);
    if (open > 0) throw Object.assign(new Error(`${open} lines are not counted yet`), { code: "23514" });
    Object.assign(c, { status: "posted", posted_at: now() });
    return { id, number: c.number, status: "posted", differences: c.lines.filter((l) => Number(l.line.counted_qty) !== l.system).length };
  },
  async cancel(id: string): Promise<CountActionResult> {
    await delay(80);
    const c = find(id);
    if (c.status !== "draft" && c.status !== "in_progress") throw stateError(c.status);
    Object.assign(c, { status: "cancelled", cancelled_at: now() });
    return { id, status: "cancelled" };
  },
};
